import { memo, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import FormInput from "../../../UI/Inputs/FormInput";
import { IJustificationFieldProps } from "./models";
import { useTranslation } from "react-i18next";
import { alternativeRequiresJustification } from "../justificationRules";

const translationPath = "components.step.";

export const JustificationField = memo(
  ({
    requiredJustificationLessThan,
    requiredJustificationOptions,
    questionAlternatives,
    hasAnswerDate,
    alwaysShowJustification,
    isRequiredJustification,
    requiredJustificationMessage,
    fieldNamePrefix,
  }: IJustificationFieldProps) => {
    const { t } = useTranslation();

    const { clearErrors, getValues, setError, setFocus, watch } =
      useFormContext();

    const alternativeFieldName = fieldNamePrefix
      ? `${fieldNamePrefix}.questionAlternative`
      : "questionAlternative";

    const justificationFieldName = fieldNamePrefix
      ? `${fieldNamePrefix}.justification`
      : "justification";

    const alternative = watch(alternativeFieldName);

    const currentAlternative = questionAlternatives.find(
      (alt) => alt.id === alternative,
    );

    const selectedOptionRequiresJustification =
      isRequiredJustification &&
      !!currentAlternative &&
      alternativeRequiresJustification(
        currentAlternative.integrationId,
        Number(currentAlternative.nrOption),
        requiredJustificationOptions,
        requiredJustificationLessThan,
      );

    useEffect(() => {
      if (hasAnswerDate || !alternative) return;

      if (!selectedOptionRequiresJustification) {
        clearErrors(justificationFieldName);
        return;
      }

      if (!getValues(justificationFieldName)?.trim()) {
        setError(justificationFieldName, {
          type: "required",
          message: requiredJustificationMessage,
        });

        const focusFrame = window.requestAnimationFrame(() => {
          setFocus(justificationFieldName);
        });

        return () => window.cancelAnimationFrame(focusFrame);
      }
    }, [
      alternative,
      clearErrors,
      getValues,
      hasAnswerDate,
      justificationFieldName,
      requiredJustificationMessage,
      selectedOptionRequiresJustification,
      setError,
      setFocus,
    ]);

    if (!alwaysShowJustification && !selectedOptionRequiresJustification) {
      return null;
    }

    return (
      <FormInput
        name={justificationFieldName}
        label={t(translationPath + "justification")}
        size="small"
        fullWidth
        multiline
        minRows={4}
        disabled={hasAnswerDate}
        inputProps={{ maxLength: 255 }}
      />
    );
  },
);
